import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import axios from 'axios';
import AOS from 'aos';
import 'aos/dist/aos.css';
import workshop from '../../images/workshop_images/Workshops.jpg';
import workshop04 from '../../images/workshop_images/workshop-poster.jpg';
import staticWorkshops, { resolveWorkshopCover } from '../../data/staticWorkshops';
import { getApiBaseUrl } from '../../utils/apiBase';

function normalizeWorkshops(payload) {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload.results)) return payload.results;
    return [];
}

function Workshop() {
    const [workshops, setWorkshops] = useState([]);
    const [loading, setLoading] = useState(true);
    const [timeLeft, setTimeLeft] = useState([]);
    // eslint-disable-next-line
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchWorkshops = async () => {
            setLoading(true);
            try {
                const baseURL = getApiBaseUrl();
                const response = await axios.get(`${baseURL}/workshops/all`);
                const list = normalizeWorkshops(response.data)
                    // hide dummy/test rows
                    .filter((w) => w?.slug && w.slug !== '444' && w.title !== '444');

                if (list.length > 0) {
                    setWorkshops(list);
                } else {
                    // API empty → show static workshops
                    setWorkshops(staticWorkshops);
                }
            } catch (err) {
                console.error('Error fetching workshops:', err);
                setError('Failed to load workshops. Showing static list.');
                setWorkshops(staticWorkshops);
            } finally {
                setLoading(false);
            }
        };

        fetchWorkshops();
        AOS.init({
            duration: 1500,
            easing: 'ease-in-out',
        });
    }, []);

    useEffect(() => {
        const updateTimer = () => {
            const newTimeLeft = workshops.map((item) => {
                const targetDate = new Date(`${item.date}T${String(item.start_time).slice(0, 8)}`);
                const now = new Date();
                const timeDifference = targetDate - now;

                if (timeDifference > 0) {
                    return {
                        days: Math.floor(timeDifference / (1000 * 60 * 60 * 24)),
                        hours: Math.floor((timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
                        minutes: Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60)),
                        seconds: Math.floor((timeDifference % (1000 * 60)) / 1000),
                    };
                }
                return { days: 0, hours: 0, minutes: 0, seconds: 0 };
            });

            setTimeLeft(newTimeLeft);
        };

        if (!workshops.length) return undefined;
        updateTimer();
        const interval = setInterval(updateTimer, 1000);
        return () => clearInterval(interval);
    }, [workshops]);

    return (
        <>
            {loading && (
                <div className="loader-container">
                    <div className="loader"></div>
                    <img src="/images/logo192.png" alt="Anuyog Icon" className="loader-icon" />
                </div>
            )}

            {!loading && (
                <>
                    <section className="workshop-section">
                        <div className="consultancy">
                            <img src={workshop} alt="Workshops Banner" />
                            <h1 className="consultancy-heading">Join Our Upcoming Workshops</h1>
                        </div>
                    </section>

                    {workshops.length === 0 && (
                        <section className="section-gapping">
                            <div className="container">
                                <p className="text-center fw-bold">No workshops available right now.</p>
                            </div>
                        </section>
                    )}

                    {workshops.map((item, index) => (
                        <section key={item.slug || item.id || index} className="upcoming-section section-gapping">
                            <div className="container">
                                <div className="upcoming">
                                    <div className="upcoming-detail" data-aos="fade-up">
                                        <div className="upcoming-image">
                                            <img
                                                src={resolveWorkshopCover(item, workshop04)}
                                                alt={`${item.title} Poster`}
                                            />
                                            <div className="close">
                                                <span>Close In:</span>
                                                <span>
                                                    {timeLeft[index]?.days || 0} days {timeLeft[index]?.hours || 0} hours{' '}
                                                    {timeLeft[index]?.minutes || 0} minutes
                                                </span>
                                            </div>
                                        </div>

                                        <div className="upcoming-info">
                                            <div className="upcoming-text">
                                                <h3 className="fw-bold text-start">{item.title}</h3>
                                                <p className="mb-4 fw-bold text-start">
                                                    <strong>{item.subtitle}</strong>
                                                </p>
                                                <ul className="text-start fw-bold">
                                                    <h5 className="fw-bold text-start">Workshop Details:</h5>
                                                    <li>
                                                        <strong>Duration:</strong> {item.duration}
                                                    </li>
                                                    <li>
                                                        <strong>Date:</strong>{' '}
                                                        {new Date(item.date).toLocaleDateString('en-GB', {
                                                            day: 'numeric',
                                                            month: 'long',
                                                            year: 'numeric',
                                                        })}
                                                    </li>
                                                    <li>
                                                        <strong>Time:</strong>{' '}
                                                        {String(item.start_time).slice(0, 5)} -{' '}
                                                        {String(item.end_time).slice(0, 5)} IST
                                                    </li>
                                                    <li>
                                                        <strong>Course Fee:</strong>{' '}
                                                        <s>Rs. {parseFloat(item.course_fee_original).toFixed(0)}</s> Rs.{' '}
                                                        {parseFloat(item.course_fee_discounted).toFixed(0)}
                                                    </li>
                                                    <li>
                                                        <strong>Language:</strong>{' '}
                                                        {(item.languages || []).join(' and ')}
                                                    </li>
                                                    <li>
                                                        <strong>Location:</strong>{' '}
                                                        {String(item.location || '').charAt(0).toUpperCase() +
                                                            String(item.location || '').slice(1)}
                                                    </li>
                                                    <li className="mt-2">
                                                        <NavLink
                                                            to={item.slug ? `/workshops/${item.slug}` : '/contact'}
                                                            onClick={() => window.scrollTo(0, 0)}
                                                        >
                                                            <button type="button" className="btn">
                                                                Register Now
                                                            </button>
                                                        </NavLink>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>
                    ))}
                </>
            )}
        </>
    );
}

export default Workshop;
